import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Card, CardContent, Grid, TextField, Button, Chip, CircularProgress, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Switch, FormControlLabel, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Avatar, Tooltip } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI } from '../../services/api';
import { Edit as EditIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';

const SuperAdminPage = () => {
    const { user } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [editOpen, setEditOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editRole, setEditRole] = useState('client');
    const [editActive, setEditActive] = useState(true);

    const isSuperAdmin = user?.role === 'superadmin';

    useEffect(() => {
        if (isSuperAdmin) {
            fetchUsers();
        }
    }, [isSuperAdmin]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const res = await adminAPI.getUsers();
            setUsers(res.data.users || []);
        } catch (e) {
            toast.error('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const openEdit = (u) => {
        setEditingUser(u);
        setEditRole(u.role);
        setEditActive(!!u.isActive);
        setEditOpen(true);
    };

    const closeEdit = () => {
        setEditOpen(false);
        setEditingUser(null);
    };

    const saveEdit = async () => {
        try {
            await adminAPI.updateUser(editingUser._id, { role: editRole, isActive: editActive });
            toast.success('User updated');
            closeEdit();
            fetchUsers();
        } catch (e) {
            toast.error('Failed to update user');
        }
    };

    const filteredSorted = useMemo(() => {
        const list = users.filter(u =>
            `${u.firstName || ''} ${u.lastName || ''} ${u.email || ''}`.toLowerCase().includes(filter.toLowerCase())
        );
        // Sort by createdAt desc, fallback to _id desc
        return list.sort((a, b) => {
            const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            if (aDate !== 0 || bDate !== 0) return bDate - aDate;
            return (b._id || '').localeCompare(a._id || '');
        });
    }, [users, filter]);

    const paged = useMemo(() => {
        const start = page * rowsPerPage;
        return filteredSorted.slice(start, start + rowsPerPage);
    }, [filteredSorted, page, rowsPerPage]);

    const handleChangePage = (_e, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    if (!isSuperAdmin) {
        return (
            <Box>
                <Typography variant="h6">Access denied</Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" fontWeight="bold">Super Admin</Typography>
                <Box>
                    <IconButton onClick={fetchUsers} disabled={loading}>
                        <RefreshIcon />
                    </IconButton>
                </Box>
            </Box>
            <Box mb={2}>
                <TextField value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search users by name or email..." fullWidth size="small" />
            </Box>
            <Card>
                <CardContent>
                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>User</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Role</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Created</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paged.map((u) => (
                                        <TableRow key={u._id} hover>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main' }}>
                                                        {(u.firstName?.[0] || '').toUpperCase()}{(u.lastName?.[0] || '').toUpperCase()}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={600}>{u.firstName} {u.lastName}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{u._id}</Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>{u.email}</TableCell>
                                            <TableCell>
                                                <Chip label={u.role} size="small" color={u.role === 'superadmin' ? 'error' : (u.role === 'admin' ? 'primary' : 'default')} />
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={u.isActive ? 'Active' : 'Inactive'} size="small" color={u.isActive ? 'success' : 'default'} variant={u.isActive ? 'filled' : 'outlined'} />
                                            </TableCell>
                                            <TableCell>
                                                {u.createdAt ? (
                                                    <>
                                                        <Typography variant="body2">{dayjs(u.createdAt).format('YYYY-MM-DD HH:mm')}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{dayjs(u.createdAt).fromNow?.() || ''}</Typography>
                                                    </>
                                                ) : (
                                                    <Typography variant="body2">—</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Edit">
                                                    <IconButton size="small" onClick={() => openEdit(u)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Button size="small" onClick={() => window.location.assign(`/dashboard/superadmin/users/${u._id}`)}>View</Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {paged.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                <Typography variant="body2" color="text.secondary">No users found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                count={filteredSorted.length}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                            />
                        </>
                    )}
                </CardContent>
            </Card>

            <Dialog open={editOpen} onClose={closeEdit} maxWidth="sm" fullWidth>
                <DialogTitle>Edit User</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <TextField label="Role" select value={editRole} onChange={(e) => setEditRole(e.target.value)}>
                            <MenuItem value="client">client</MenuItem>
                            <MenuItem value="admin">admin</MenuItem>
                            <MenuItem value="superadmin">superadmin</MenuItem>
                        </TextField>
                        <FormControlLabel control={<Switch checked={editActive} onChange={(e) => setEditActive(e.target.checked)} />} label="Active" />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeEdit}>Cancel</Button>
                    <Button variant="contained" onClick={saveEdit}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SuperAdminPage;


